// @flow

import {
  getPickupRateApi,
  getPickupMethodApi,
  createPickupRateApi,
  deletePickupRateApi,
  updatePickupRateApi,
  updateEnabledPickupMethodApi,
  endpoints,
} from './api';
import {ofType, ActionsObservable} from 'redux-observable';
import {map, switchMap} from 'rxjs/operators';
import {
  ActionCreator,
  createReducer,
  buildCatchError,
} from '../../../../store/utils';
import {buildRoute} from '../../../../http';

import type {Action} from '../../../../store/utils';
import type {
  Address,
  PickupMethod,
  PickupRateRequest,
} from '../../../../entities';

export type PickupConfigAction = Action & {
  error?: string,
  pickupRateRequest?: PickupRateRequest,
  adminRateId?: string,
  enabled?: boolean,
};

export type PickupConfigState = {
  pickupMethod?: PickupMethod,
  pickupRate?: Address,

  isFetchingPickupMethods?: boolean,
  fetchingPickupSuccess?: boolean,
  fetchingPickupError?: string,

  isSaving?: boolean,
  savingPickupSuccess?: boolean,
  savingPickupError?: string,

  deletePickupSuccess?: boolean,
  deletePickupError?: boolean,

  savingPickupEnabledSuccess?: boolean,
  savingPickupEnabledError?: string,

  messageError?: string,
};

export const initialState: PickupConfigAction = {
  pickupMethod: undefined,
  isFetchingPickupMethods: true,
};

export class EnablePickupMethod extends ActionCreator<PickupConfigAction> {}
export class EnablePickupMethodSuccess extends ActionCreator<PickupConfigAction> {}
export class EnablePickupMethodError extends ActionCreator<PickupConfigAction> {}

export class SavePickupMethod extends ActionCreator<PickupConfigAction> {}
export class SavePickupMethodSuccess extends ActionCreator<PickupConfigAction> {}
export class SavePickupMethodError extends ActionCreator<PickupConfigAction> {}

export class FetchPickupMethod extends ActionCreator<PickupConfigAction> {}
export class FetchPickupMethodSuccess extends ActionCreator<PickupConfigAction> {}
export class FetchPickupMethodError extends ActionCreator<PickupConfigAction> {}

export class FetchPickupRate extends ActionCreator<PickupConfigAction> {}
export class FetchPickupRateSuccess extends ActionCreator<PickupConfigAction> {}
export class FetchPickupRateError extends ActionCreator<PickupConfigAction> {}

export class UpdatePickupRate extends ActionCreator<PickupConfigAction> {}
export class UpdatePickupRateSuccess extends ActionCreator<PickupConfigAction> {}
export class UpdatePickupRateError extends ActionCreator<PickupConfigAction> {}

export class DeletePickupRate extends ActionCreator<PickupConfigAction> {}
export class DeletePickupRateSuccess extends ActionCreator<PickupConfigAction> {}
export class DeletePickupRateError extends ActionCreator<PickupConfigAction> {}

export class ResetState extends ActionCreator<PickupConfigAction> {}

export const actions = {
  enabledPickupMethod: (payload: PickupConfigAction) =>
    new EnablePickupMethod(payload),
  getPickupRate: (payload: PickupConfigAction) => new FetchPickupRate(payload),
  getEnabledPickupMethod: (payload: PickupConfigAction) =>
    new FetchPickupMethod(payload),
  savePickupMethod: (payload: PickupConfigAction) =>
    new SavePickupMethod(payload),
  updatePickupRate: (payload: PickupConfigAction) =>
    new UpdatePickupRate(payload),
  deletePickupRate: (payload: PickupConfigAction) =>
    new DeletePickupRate(payload),
  resetState: () => new ResetState(),
};

export default createReducer<PickupConfigState>(initialState, {
  [FetchPickupMethod.type]: (state: PickupConfigState) => ({
    ...state,
    isFetchingPickupMethods: true,
    savingPickupSuccess: false,
    savingPickupError: false,
    deletePickupSuccess: false,
    deletePickupError: false,
  }),
  [FetchPickupMethodSuccess.type]: (
    state: PickupConfigState,
    action: PickupConfigAction
  ) => {
    return {
      ...state,
      pickupMethod: action.pickupMethod,
      isFetchingPickupMethods: false,
      fetchingPickupSuccess: true,
      fetchingPickupError: false,
      savingPickupSuccess: false,
      savingPickupError: false,
      deletePickupSuccess: false,
      deletePickupError: false,
      savingPickupEnabledSuccess: false,
      savingPickupEnabledError: false,
    };
  },
  [FetchPickupMethodError.type]: (
    state: PickupConfigState,
    action: PickupConfigAction
  ) => ({
    ...state,
    isFetchingPickupMethods: false,
    fetchingPickupSuccess: false,
    fetchingPickupError: true,
  }),
  [FetchPickupRate.type]: (state: PickupConfigState) => ({
    ...state,
    isFetchingPickupMethods: true,
    savingPickupSuccess: false,
    savingPickupError: false,
    deletePickupSuccess: false,
    deletePickupError: false,
  }),
  [FetchPickupRateSuccess.type]: (
    state: PickupConfigState,
    action: PickupConfigAction
  ) => {
    return {
      ...state,
      pickupRate: action.pickupRate,
      isFetchingPickupMethods: false,
      fetchingPickupSuccess: true,
      fetchingPickupError: false,
    };
  },
  [FetchPickupRateError.type]: (state: PickupConfigState) => ({
    ...state,
    isFetchingPickupMethods: false,
    fetchingPickupSuccess: false,
    fetchingPickupError: true,
  }),
  [SavePickupMethod.type]: (state: PickupConfigState) => ({
    ...state,
    isSaving: true,
    savingPickupSuccess: false,
    savingPickupError: false,
  }),
  [SavePickupMethodSuccess.type]: (
    state: PickupConfigState,
    action: PickupConfigAction
  ) => {
    return {
      ...state,
      isSaving: false,
      savingPickupSuccess: true,
      savingPickupError: false,
    };
  },
  [SavePickupMethodError.type]: (state: PickupConfigState) => ({
    ...state,
    isSaving: false,
    savingPickupSuccess: false,
    savingPickupError: true,
  }),
  [UpdatePickupRate.type]: (state: PickupConfigState) => ({
    ...state,
    isSaving: true,
    savingPickupSuccess: false,
    savingPickupError: false,
  }),
  [UpdatePickupRateSuccess.type]: (state: PickupConfigState) => ({
    ...state,
    isSaving: false,
    savingPickupSuccess: true,
    savingPickupError: false,
  }),
  [UpdatePickupRateError.type]: (
    state: PickupConfigState,
    action: PickupConfigAction
  ) => ({
    ...state,
    isSaving: false,
    savingPickupSuccess: false,
    savingPickupError: true,
    messageError: action.error,
  }),
  [DeletePickupRate.type]: (state: PickupConfigState) => ({
    ...state,
    isSaving: true,
    deletePickupSuccess: false,
    deletePickupError: false,
  }),
  [DeletePickupRateSuccess.type]: (
    state: PickupConfigState,
    action: PickupConfigAction
  ) => {
    return {
      ...state,
      isSaving: false,
      deletePickupSuccess: true,
      deletePickupError: false,
    };
  },
  [DeletePickupRateError.type]: (
    state: PickupConfigState,
    action: PickupConfigAction
  ) => ({
    ...state,
    isSaving: false,
    deletePickupSuccess: false,
    deletePickupError: true,
    messageError: action.error,
  }),
  [ResetState.type]: (state: PickupConfigState) => ({
    ...state,
    pickupRate: undefined,
    isFetchingPickupMethods: false,
    fetchingPickupSuccess: false,
    fetchingPickupError: false,
    isSaving: false,
    savingPickupSuccess: false,
    savingPickupError: false,
    deletePickupSuccess: false,
    deletePickupError: false,
    savingPickupEnabledSuccess: false,
    savingPickupEnabledError: false,
    messageError: '',
  }),
  [EnablePickupMethod.type]: (state: PickupConfigState) => ({
    ...state,
    isSaving: true,
    savingPickupEnabledSuccess: false,
    savingPickupEnabledError: false,
  }),
  [EnablePickupMethodSuccess.type]: (state: PickupConfigState) => ({
    ...state,
    isSaving: false,
    savingPickupEnabledSuccess: true,
    savingPickupEnabledError: false,
  }),
  [EnablePickupMethodError.type]: (
    state: PickupConfigState,
    action: PickupConfigAction
  ) => ({
    ...state,
    isSaving: false,
    savingPickupEnabledSuccess: false,
    savingPickupEnabledError: true,
    messageError: action.error,
  }),
});

export function enabledPickupMethodEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EnablePickupMethod.type),
    switchMap(({enabled}: PickupConfigAction) => {
      return updateEnabledPickupMethodApi({enabled}).pipe(
        map(() => new EnablePickupMethodSuccess()),
        buildCatchError(EnablePickupMethodError)
      );
    })
  );
}

export function getLogisticPickupMethodEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(
      FetchPickupMethod.type,
      SavePickupMethodSuccess.type,
      UpdatePickupRateSuccess.type,
      DeletePickupRateSuccess.type,
      EnablePickupMethodSuccess.type
    ),
    switchMap(() => {
      return getPickupMethodApi().pipe(
        map(
          pickupMethod =>
            new FetchPickupMethodSuccess({
              pickupMethod,
            })
        ),
        buildCatchError(FetchPickupMethodError)
      );
    })
  );
}

export function getLogisticPickupRateEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchPickupRate.type),
    switchMap(({adminRateId}: PickupConfigAction) => {
      return getPickupRateApi(undefined, {
        route: buildRoute(endpoints.GET_PICKUP_RATE, [adminRateId]),
      }).pipe(
        map(pickupRate => new FetchPickupRateSuccess({pickupRate})),
        buildCatchError(FetchPickupRateError)
      );
    })
  );
}

export function logisticSavePickupRateEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SavePickupMethod.type),
    switchMap(({pickupRateRequest}: PickupConfigAction) => {
      return createPickupRateApi(pickupRateRequest).pipe(
        map(() => new SavePickupMethodSuccess()),
        buildCatchError(SavePickupMethodError)
      );
    })
  );
}

export function logisticUpdatePickupRateEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(UpdatePickupRate.type),
    switchMap(({pickupRateRequest, adminRateId}: PickupConfigAction) => {
      return updatePickupRateApi(pickupRateRequest, {
        route: buildRoute(endpoints.UPDATE_PICKUP_RATE, [adminRateId]),
      }).pipe(
        map(() => new UpdatePickupRateSuccess()),
        buildCatchError(UpdatePickupRateError)
      );
    })
  );
}

export function logisitcDeletePickupRateEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(DeletePickupRate.type),
    switchMap(({adminRateId}: PickupConfigAction) => {
      return deletePickupRateApi({
        route: buildRoute(endpoints.DELETE_PICKUP_RATE, [adminRateId]),
      }).pipe(
        map(() => new DeletePickupRateSuccess({adminRateId})),
        buildCatchError(DeletePickupRateError)
      );
    })
  );
}
